import React from 'react';
import PropTypes from 'prop-types'; // Use PropTypes for type checking in plain JavaScript

/**
 * @typedef {Object} IconButtonProps
 * @property {React.ElementType} icon - The React component for the icon (e.g., CrossIcon from lucide-react).
 * @property {string} label - The text label for the button, visible on larger screens.
 * @property {() => void} onClick - The function to be executed when the button is clicked.
 * @property {string} [bgColor='bg-[var(--primary-bg-color)]'] - Tailwind class for the button's background color.
 * @property {string} [hoverColor='hover:bg-orange-600'] - Tailwind class for the button's hover background color.
 * @property {boolean} [border=false] - If true, adds a border to the button.
 * @property {string} [color=''] - A color value (e.g., 'red', '#f00') passed to the icon's `fill` style attribute.
 * @property {string} [textColor] - Tailwind class for the button's text color.
 * @property {boolean} [disabled] - If true, disables the button, reducing opacity and disabling clicks.
 */

/**
 * A highly customizable, responsive button that displays an icon and a label.
 * - Shows the icon only on small screens.
 * - Shows the label on larger screens.
 *
 * @param {IconButtonProps} props - The properties for the button component.
 * @returns {JSX.Element} The rendered button component.
 */
const IconButton = ({
    icon: Icon,
    label,
    onClick,
    bgColor = 'bg-[var(--primary-bg-color)]',
    hoverColor = 'hover:bg-orange-600',
    border = false,
    color = '',
    disabled,
    textColor
}) => {
    /**
     * @type {string}
     */
    const buttonClasses = `
        hover:text-white 
        text-[var(--primary-text-color)] 
        px-4 py-2 
        rounded-lg 
        flex items-center 
        sm:space-x-2 
        cursor-pointer 
        ${bgColor} 
        ${textColor || ''} 
        ${hoverColor} 
        ${border ? 'border border-[var(--border-color)]' : ''} 
        ${disabled ? "opacity-[0.5] cursor-not-allowed pointer-events-none" : ""} 
        shadow
    `;

    return (
        <button
            className={buttonClasses}
            onClick={onClick}
            disabled={disabled}
        >
            {/* The icon is shown on all screens based on the className "sm:block" (which should probably be just block or the icon will only show on medium screens and above as the label will also show) */}
            {Icon && (
                <Icon
                    // NOTE: The current classes 'w-4 h-4 sm:block' means the icon is hidden by default and only shows on screens >= sm.
                    // If you intend for it to be visible on all screens (or only on small screens), you might need to adjust classes like 'sm:hidden' or simply 'block'.
                    className="w-4 h-4 sm:block"
                    style={{ fill: color || '' }} // Pass color prop to the icon
                />
            )}

            {/* The label is hidden on small screens and shown on screens >= sm */}
            <span className={`text-sm font-medium sm:block hidden text-${color}`}>
                {label}
            </span>
        </button>
    );
};

export default IconButton;